import { createRemoteJWKSet, jwtVerify } from 'jose';

/**
 * Reassembles a chunked cookie given a chunk count and a lookup function.
 * Shared core used by both the client-side (document.cookie) and
 * server-side (parsed Cookie header) readers, which differ only in how
 * they look up an individual cookie value by name.
 */
const reassembleChunks = (count, getChunk) => {
  let assembled = '';
  for (let i = 0; i < count; i += 1) {
    const chunk = getChunk(i);
    if (!chunk) {
      return undefined;
    }
    assembled += chunk;
  }
  return assembled;
};

/**
 * Reads and reassembles a chunked cookie from document.cookie.
 * If {name}_count exists, reads {name}_0, {name}_1, ... and concatenates.
 * Otherwise falls back to reading the single {name} cookie.
 */
const getChunkedCookie = cookieName => {
  const getCookie = name => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return undefined;
  };

  const countStr = getCookie(`${cookieName}_count`);

  if (!countStr) {
    return getCookie(cookieName);
  }

  // eslint-disable-next-line radix
  const count = parseInt(countStr, 10);
  if (Number.isNaN(count) || count <= 0) {
    return getCookie(cookieName);
  }

  const assembled = reassembleChunks(count, i => getCookie(`${cookieName}_${i}`));
  if (assembled === undefined) {
    console.error(`[auth] Missing cookie chunk: ${cookieName}`);
  }
  return assembled;
};

/**
 * Clears a cookie and all its chunks (if any) client-side. Mirrors the
 * Worker's own deleteChunkedCookie -- needed because 401 handlers here
 * clear cookies directly rather than going through a logout redirect.
 */
const clearChunkedCookie = cookieName => {
  const expire = 'Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = `${cookieName}=; expires=${expire}; path=/`;

  const getCookie = name => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    return parts.length === 2 ? parts.pop().split(';').shift() : undefined;
  };

  const countStr = getCookie(`${cookieName}_count`);
  // eslint-disable-next-line radix
  const count = countStr ? parseInt(countStr, 10) : 0;

  if (!Number.isNaN(count) && count > 0) {
    document.cookie = `${cookieName}_count=; expires=${expire}; path=/`;
    for (let i = 0; i < count; i += 1) {
      document.cookie = `${cookieName}_${i}=; expires=${expire}; path=/`;
    }
  }
};

/**
 * Clears both id_token and the legacy CF_Authorization cookie (and their
 * chunks). Use on a 401 instead of a bare Cookies.remove('CF_Authorization'),
 * which would leave chunk cookies orphaned once a token exceeds 3.8KB.
 */
const clearTokenCookies = () => {
  clearChunkedCookie('id_token');
  clearChunkedCookie('CF_Authorization');
};

/**
 * Reads the id_token (chunked), falling back to CF_Authorization (legacy, also chunked).
 */
const getToken = () => getChunkedCookie('id_token') || getChunkedCookie('CF_Authorization');

const getLoginUrl = (origin, clientId) => `${origin}/af/sso/new/login?client_id=${clientId}&redirect_uri=${encodeURIComponent(`${origin}/af/sso/new/authorize`)}`;

const getLogoutUrl = (origin, clientId, postLogoutRedirectUri) => {
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append('client_id', clientId);
  if (postLogoutRedirectUri) {
    urlSearchParams.append('post_logout_redirect_uri', postLogoutRedirectUri);
  }
  return `${origin}/af/sso/new/logout?${urlSearchParams.toString()}`;
};

const getAuthTokenPayload = async (token, origin) => {
  const key = createRemoteJWKSet(new URL(`${origin}/af/sso/.well-known/jwks.json`));

  return jwtVerify(token, key).then(d => d.payload).catch(() => null);
};

const isAuthenticated = async (token, origin) => {
  if (!token) return null;

  const user = await getAuthTokenPayload(token, origin);

  // [ASK_TEAM]: Which are the roles that can access the app?
  if (!user || !['admin', 'recruiter'].some(role => user.roles.includes(role))) return null;

  return user;
};

export { reassembleChunks, getChunkedCookie, clearTokenCookies, getAuthTokenPayload, isAuthenticated, getLoginUrl, getToken, getLogoutUrl };
